import React, { useState, useEffect, useContext } from "react";
import { doc, onSnapshot } from "firebase/firestore";
import UserContext from "../assets/user_context";
import { useAuth } from "./use-auth";
import MaintenancePage from "../locations/maintenance/MaintenancePage";
import { CircularProgress, Box } from "@mui/material";

// Debug logging - set to true to enable verbose logging
const DEBUG_LOGGING = false;
const debugLog = (...args) => DEBUG_LOGGING && console.log(...args);

const MaintenanceGuard = ({ children }) => {
  const { userData, user, userStillLoading } = useContext(UserContext);
  const [maintenanceMode, setMaintenanceMode] = useState(false);
  const [loadingMaintenanceMode, setLoadingMaintenanceMode] = useState(true);
  const [scheduledMaintenance, setScheduledMaintenance] = useState(null);
  const [optimisticMaintenanceMode, setOptimisticMaintenanceMode] =
    useState(false);
  const authHook = useAuth();

  useEffect(() => {
    if (authHook.db) {
      const maintenanceRef = doc(authHook.db, "system", "maintenance");
      const unSubscribe = onSnapshot(
        maintenanceRef,
        (snapshot) => {
          if (snapshot.exists()) {
            const data = snapshot.data();
            const isEnabled = data.enabled || false;
            debugLog("🚧 MaintenanceGuard: Received maintenance data:", {
              enabled: isEnabled,
              scheduledDateTime: data.scheduledDateTime,
              lastChangedBy: data.lastChangedBy,
              autoTriggered: data.autoTriggered,
            });
            setMaintenanceMode(isEnabled);

            // Handle scheduled maintenance for optimistic activation
            if (data.scheduledDateTime && !isEnabled) {
              const scheduledTime = data.scheduledDateTime.toDate
                ? data.scheduledDateTime.toDate()
                : new Date(data.scheduledDateTime);
              setScheduledMaintenance({
                scheduledDateTime: scheduledTime,
                message:
                  data.maintenanceMessage ||
                  "Scheduled maintenance will begin shortly.",
              });
            } else {
              setScheduledMaintenance(null);
            }

            // If maintenance is now enabled, clear optimistic mode
            if (isEnabled) {
              setOptimisticMaintenanceMode(false);
            }
          } else {
            debugLog("🚧 MaintenanceGuard: No maintenance document exists");
            setMaintenanceMode(false);
            setScheduledMaintenance(null);
            setOptimisticMaintenanceMode(false);
          }
          setLoadingMaintenanceMode(false);
        },
        (error) => {
          console.error("Error listening to maintenance mode:", error);
          setMaintenanceMode(false);
          setLoadingMaintenanceMode(false);
        },
      );

      return () => unSubscribe();
    }
  }, [authHook.db]);

  // Optimistic maintenance mode countdown
  useEffect(() => {
    if (!scheduledMaintenance) {
      setOptimisticMaintenanceMode(false);
      return;
    }

    const checkOptimisticMaintenance = () => {
      const now = new Date();
      const scheduledTime = scheduledMaintenance.scheduledDateTime;

      // If we've reached the scheduled time, optimistically enable maintenance mode
      if (now >= scheduledTime) {
        debugLog(
          "🚀 MaintenanceGuard: Optimistically enabling maintenance mode - countdown reached 0",
        );
        setOptimisticMaintenanceMode(true);
      }
    };

    // Check immediately
    checkOptimisticMaintenance();

    // Then check every second for smooth transition
    const interval = setInterval(checkOptimisticMaintenance, 1000);

    return () => clearInterval(interval);
  }, [scheduledMaintenance]);

  // Don't interfere with the normal loading process - let UserProvider handle loading states
  // Only check maintenance mode if we have basic user data loaded
  const hasBasicUserData = user && userData && !userStillLoading;

  // If we don't have basic user data yet, let the normal loading flow continue
  if (!hasBasicUserData) {
    return children;
  }

  // If still checking maintenance mode, don't show maintenance page yet
  // This prevents showing maintenance to superAdmins before we know the mode
  if (loadingMaintenanceMode) {
    return children;
  }

  // Check both real maintenance mode and optimistic maintenance mode
  const isInMaintenanceMode = maintenanceMode || optimisticMaintenanceMode;

  debugLog("🚧 MaintenanceGuard: Final check:", {
    realMaintenanceMode: maintenanceMode,
    optimisticMaintenanceMode: optimisticMaintenanceMode,
    combinedMaintenanceMode: isInMaintenanceMode,
    userIsSuperAdmin: userData?.superAdmin,
    userEmail: userData?.email,
    willShowMaintenancePage: isInMaintenanceMode && !userData?.superAdmin,
  });

  if (isInMaintenanceMode && !userData.superAdmin) {
    const maintenanceType =
      optimisticMaintenanceMode && !maintenanceMode ? "optimistic" : "real";
    debugLog(
      `🚧 Maintenance mode enabled (${maintenanceType}) - showing maintenance page to non-superAdmin user`,
    );
    return <MaintenancePage />;
  }

  // Otherwise, render normal app (maintenance disabled OR user is superAdmin)
  debugLog("🚧 MaintenanceGuard: Allowing normal app access");
  return children;
};

export default MaintenanceGuard;
